import { useEffect, useRef } from "react";
import { Modal } from "antd";
import axios from "axios";
import storage from "../utils/storage";

const TOKEN_EXPIRY_MS = 8 * 60 * 60 * 1000;     // 8 hours
const REFRESH_BEFORE_MS = 30 * 60 * 1000;         // rotate 30 min before expiry
const IDLE_LOGOUT_MS = 30 * 60 * 1000;            // auto-logout if idle 30 min past expiry
const CHECK_INTERVAL_MS = 60 * 1000;              // check every minute

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"];

function clearSession() {
  ["token", "userId", "tokenExpiresAt"].forEach((key) => storage.remove(key));
}

function showSessionExpiredModal() {
  Modal.destroyAll();
  Modal.warning({
    title: "Session Expired",
    content: "Your session has expired due to inactivity. Please log in again.",
    okText: "Log In",
    onOk: () => {
      clearSession();
      window.location.href = "/";
    },
    maskClosable: false,
    keyboard: false,
  });
}

export function useSessionManager() {
  const lastActivityRef = useRef(Date.now());
  const isRefreshingRef = useRef(false);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, updateActivity));
  }, []);

  // Session check timer
  useEffect(() => {
    const timer = setInterval(async () => {
      const isLoggedIn = !!storage.get("token");
      if (!isLoggedIn) return;

      const expiresAt = parseInt(storage.get("tokenExpiresAt") || "0");
      if (!expiresAt) return;

      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      const idleTime = now - lastActivityRef.current;

      if (timeUntilExpiry <= 0) {
        if (idleTime >= IDLE_LOGOUT_MS) {
          showSessionExpiredModal();
        }
        return;
      }

      if (timeUntilExpiry <= REFRESH_BEFORE_MS && !isRefreshingRef.current) {
        if (idleTime < REFRESH_BEFORE_MS) {
          isRefreshingRef.current = true;
          try {
            const res = await axios.post(
              `${BASE_URL}/api/login/rotateRefreshToken`,
              {},
              { withCredentials: true }
            );
            const rawData = res.data?.data;
            const newToken = res.data?.token || (typeof rawData === "string" ? rawData : rawData?.token);
            if (newToken) {
              storage.set("token", newToken);
              storage.set("tokenExpiresAt", (Date.now() + TOKEN_EXPIRY_MS).toString());
            }
          } catch {
            // Will be handled by interceptor on next request
          } finally {
            isRefreshingRef.current = false;
          }
        }
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);
}
